import { HttpParams, HttpResourceRequest } from '@angular/common/http';
import { FlickrPhoto } from '@model/flickr';
import { Photo } from '@model/photo';
import { PhotoAlbum } from '@model/photo-album';
import { Constants } from '@shared/constants';

/**
 * Converts a Flickr date string ("YYYY-MM-DD HH:mm:ss") into a `Date`.
 *
 * Flickr exposes dates with a space between date and time; this helper
 * normalizes the format to an ISO-like string before constructing the
 * JavaScript `Date`.
 *
 * @param date Date string as returned by the Flickr API.
 * @returns A JavaScript `Date` instance representing the same moment in time.
 */
export const convertFlickrDate = (date: string): Date =>
  new Date(date.replace(/(.+) (.+)/, '$1T$2Z'));

/**
 * Builds the base Flickr photo URL for an album or photo.
 *
 * The URL does not include any size suffix or file extension; those are
 * added by `createFlickrPhotoUrl`.
 *
 * @param flickPhoto Flickr photo metadata.
 * @param type Whether the URL is for an `album` or a single `photo`.
 * @returns The base URL for the requested Flickr resource.
 */
const createFlickrPhotoBaseUrl = (
  flickPhoto: FlickrPhoto,
  type: 'album' | 'photo',
): string =>
  `https://farm${flickPhoto.farm}.static.flickr.com/${flickPhoto.server}/${type === 'album' ? flickPhoto.primary : flickPhoto.id}_${flickPhoto.secret}`;

/**
 * Constructs a full Flickr image URL for a given size.
 *
 * Uses the base URL from `createFlickrPhotoBaseUrl` and appends a size
 * suffix (`_b` for hi-res, `_m` for thumbnail, none for default image)
 * followed by `.jpg`.
 *
 * @param flickPhoto Flickr photo metadata.
 * @param type Whether the URL is for an `album` or a single `photo`.
 * @param imageType Desired image variant: `hiRes`, `image` or `thumbnail`.
 * @returns A complete image URL to the requested Flickr resource.
 */
const createFlickrPhotoUrl = (
  flickPhoto: FlickrPhoto,
  type: 'album' | 'photo',
  imageType: 'hiRes' | 'image' | 'thumbnail',
): string => {
  const baseUrl = createFlickrPhotoBaseUrl(flickPhoto, type);

  const suffix =
    imageType === 'hiRes' ? '_b' : imageType === 'thumbnail' ? '_m' : '';

  return `${baseUrl}${suffix}.jpg`;
};
/**
 * Maps a Flickr photoset response to a `PhotoAlbum` model.
 *
 * Derives basic information (id, count, title, description) and builds
 * image URLs for default, thumbnail and hi-res images.
 *
 * @param flickPhoto Flickr photoset data.
 * @returns A `PhotoAlbum` instance representing the Flickr album.
 */
export const createPhotoAlbum = (flickPhoto: FlickrPhoto): PhotoAlbum => ({
  uuid: flickPhoto.id,
  count: flickPhoto.photos,
  dateTaken: convertFlickrDate(flickPhoto.primary_photo_extras.datetaken),
  description: flickPhoto.description._content,
  imageUrl: createFlickrPhotoUrl(flickPhoto, 'album', 'image'),
  thumbnailUrl: createFlickrPhotoUrl(flickPhoto, 'album', 'thumbnail'),
  hiResImageUrl: createFlickrPhotoUrl(flickPhoto, 'album', 'hiRes'),
  title:
    typeof flickPhoto.title === 'string'
      ? flickPhoto.title
      : flickPhoto.title._content,
  pages: Math.ceil(flickPhoto.photos / Constants.FLICKS_ALBUM_PHOTOS_PER_PAGE),
});

/**
 * Maps a Flickr photo response to a `Photo` model.
 *
 * Converts the Flickr `datetaken` field and constructs URLs for the
 * different image sizes.
 *
 * @param flickPhoto Flickr photo data.
 * @returns A `Photo` instance ready for use in the app.
 */
export const createPhoto = (flickPhoto: FlickrPhoto): Photo => ({
  uuid: flickPhoto.id,
  dateTaken: convertFlickrDate(flickPhoto.datetaken),
  imageUrl: createFlickrPhotoUrl(flickPhoto, 'photo', 'image'),
  thumbnailUrl: createFlickrPhotoUrl(flickPhoto, 'photo', 'thumbnail'),
  hiResImageUrl: createFlickrPhotoUrl(flickPhoto, 'photo', 'hiRes'),
  title:
    typeof flickPhoto.title === 'string'
      ? flickPhoto.title
      : flickPhoto.title._content,
  description: flickPhoto.description._content ?? undefined,
});

/**
 * Creates the default `HttpParams` for Flickr API requests.
 *
 * Injects the common parameters such as API key, user id and JSON format.
 *
 * @param method Flickr API method name.
 * @returns An `HttpParams` instance pre-populated with default values.
 */
export const createDefaultFlickrHttpParams = (method: string): HttpParams =>
  new HttpParams()
    .append('method', method)
    .append('api_key', Constants.FLICKR_API_KEY)
    .append('user_id', Constants.FLICKR_USER_ID)
    .append('format', 'json')
    .append('nojsoncallback', '1');

/**
 * Builds an `HttpResourceRequest` for fetching photos of a specific album.
 *
 * Uses the Flickr configuration constants and default params while
 * adding album- and paging-specific parameters.
 *
 * @param photoAlbum Album whose photos should be fetched.
 * @param page Page number to request.
 * @returns A resource request describing the Flickr HTTP call.
 */
export const createFlickrPhotosResoureRequest = (
  photoAlbum: PhotoAlbum,
  page: number,
): HttpResourceRequest => {
  const params = createDefaultFlickrHttpParams(
    Constants.FLICKR_ALBUM_PHOTOS_OPERATION,
  )
    .append('photoset_id', photoAlbum.uuid)
    .append('privacy_filter', '1')
    .append('extras', 'description,date_taken')
    .append('per_page', Constants.FLICKS_ALBUM_PHOTOS_PER_PAGE)
    .append('page', page);

  return {
    url: Constants.FLICKR_URL,
    method: 'GET',
    params,
  };
};

/**
 * Creates a virtual "favorites" photo album from a list of photos.
 *
 * Uses the first photo as the visual representative and aggregates the
 * remaining metadata (count, title, description) into a `PhotoAlbum`
 * with a fixed UUID.
 *
 * @param photos List of photos that make up the favorites album.
 * @param title Title for the favorites album.
 * @param description Description for the favorites album.
 * @returns A `PhotoAlbum` with fallback images when there are no photos.
 */
export const createFavoritesAlbum = (
  photos: Photo[],
  title: string,
  description: string,
): PhotoAlbum => {
  const photo = photos[0];

  const favoritesAlbum: PhotoAlbum = {
    uuid: Constants.FAVORITES_ALBUM_UUID,
    count: photos.length || 0,
    dateTaken: undefined,
    description,
    hiResImageUrl:
      photo?.hiResImageUrl ?? 'assets/images/ad6-logo-mountain.png',
    imageUrl: photo?.imageUrl ?? 'assets/images/ad6-logo-mountain.png',
    pages: 0,
    thumbnailUrl: photo?.thumbnailUrl ?? 'assets/images/ad6-logo-mountain.png',
    title,
  };

  return favoritesAlbum;
};
